import { Button } from "@medme/ui"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { getLeadBusinessesAccess, useUserBusinesses } from "@/entities/business"
import {
	useMarketingLeadsGetLeadsSummary,
	useMarketingLeadsListLeads,
} from "@/shared/api/generated/marketing-leads/marketing-leads"
import type { Lead } from "@/shared/api/generated/types/marketing"
import { selectLeadsData, selectLeadsSummaryData } from "../model/api-selectors"
import type {
	LeadsRouteSearch,
	UpdateLeadsRouteSearch,
} from "../model/leads-search"
import { useLeadsExport } from "../model/use-leads-export"
import { useLeadsFilters } from "../model/use-leads-filters"
import { LeadDetailsDialog } from "./LeadDetailsDialog"
import { LeadsFilters } from "./LeadsFilters"
import { LeadsHeader } from "./LeadsHeader"
import { LeadsPagination } from "./LeadsPagination"
import { LeadsTable } from "./LeadsTable"

type LeadsPageProps = {
	routeSearch: LeadsRouteSearch
	onRouteSearchChange: UpdateLeadsRouteSearch
}

export function LeadsPage({
	routeSearch,
	onRouteSearchChange,
}: LeadsPageProps) {
	const { t } = useTranslation()
	const {
		data: access,
		isLoading: accessLoading,
		isError: accessError,
		refetch: refetchAccess,
	} = useUserBusinesses()
	const filters = useLeadsFilters(routeSearch, onRouteSearchChange)
	const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
	const businesses = access?.businesses ?? []
	const leadAccess = getLeadBusinessesAccess(businesses)
	const { canView, canExport } = leadAccess

	const leads = useMarketingLeadsListLeads(
		{
			...filters.filterParams,
			page: filters.page,
			pageSize: filters.pageSize,
			sort: "createdAt",
			order: "desc",
		},
		{ query: { enabled: canView, select: selectLeadsData } },
	)
	const summary = useMarketingLeadsGetLeadsSummary(undefined, {
		query: { enabled: canView, select: selectLeadsSummaryData },
	})
	const { exportLeads, isExporting } = useLeadsExport(filters.filterParams)
	const totalPages = Math.max(
		1,
		Math.ceil((leads.data?.paging.total ?? 0) / filters.pageSize),
	)

	useEffect(() => {
		if (!canView || !leads.data || leads.isError || filters.page <= totalPages)
			return

		onRouteSearchChange({
			page: totalPages === 1 ? undefined : totalPages,
		})
	}, [
		canView,
		filters.page,
		leads.data,
		leads.isError,
		onRouteSearchChange,
		totalPages,
	])

	if (accessLoading) {
		return (
			<output className="block p-8">
				<Loader2 className="animate-spin" />
				<span className="sr-only">{t("leads.loading")}</span>
			</output>
		)
	}
	if (accessError) {
		return (
			<div
				className="grid justify-items-center gap-3 p-8 text-center"
				role="alert"
			>
				<h1 className="text-2xl font-semibold">{t("leads.error.access")}</h1>
				<Button
					className="h-auto py-2"
					variant="outline"
					onClick={() => refetchAccess()}
				>
					{t("leads.actions.retry")}
				</Button>
			</div>
		)
	}
	if (!canView) {
		return (
			<div className="p-8">
				<h1 className="text-2xl font-semibold">
					{t("leads.accessDenied.title")}
				</h1>
				<p className="mt-2 text-mm-muted-fg">
					{t("leads.accessDenied.description")}
				</p>
			</div>
		)
	}

	return (
		<div className="space-y-5 p-4 sm:p-5 lg:p-7">
			<LeadsHeader
				total={summary.data?.total}
				newCount={summary.data?.new}
				isSummaryLoading={summary.isPending || summary.isFetching}
				isSummaryError={summary.isError}
				onRetrySummary={() => summary.refetch()}
			/>
			<LeadsFilters
				filters={filters}
				canExport={canExport}
				isExporting={isExporting}
				onExport={exportLeads}
			/>
			<div className="overflow-hidden rounded-2xl border bg-mm-card shadow-xs">
				<LeadsTable
					leads={leads.data?.data ?? []}
					isPending={leads.isPending || leads.isFetching}
					isError={leads.isError}
					onRetry={() => leads.refetch()}
					onResetFilters={filters.reset}
					onOpenLead={setSelectedLead}
				/>
			</div>
			<LeadsPagination
				page={filters.page}
				pageSize={filters.pageSize}
				totalPages={totalPages}
				isFetching={leads.isFetching}
				onPageChange={filters.setPage}
				onPageSizeChange={filters.setPageSize}
			/>
			{selectedLead && (
				<LeadDetailsDialog
					key={`${selectedLead.id}:${selectedLead.updatedAt}`}
					lead={selectedLead}
					canEdit={leadAccess.canEditBusiness(selectedLead.businessId)}
					onClose={() => setSelectedLead(null)}
				/>
			)}
		</div>
	)
}
